import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'actions';
import clsx from 'clsx';
import moment from 'moment';
import { 
	Button, 
	Card, 
	Grid2 as Grid,
	CardHeader, 
	CardContent, 
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Chip,
	Alert,
	Box
} from '@mui/material';
import { FilePicker } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';

import { 
	SetFormState, 
	ProcessMocnWork,
	ProcessEnodebPairingWork,
	GenerateRegenerationAudit,
	RunEricssonAudit,
	ProcessAlarmsDiff,
	RunNokiaDescriptionChecks,
	RunNokiaDescriptionDiff
} from '../../actions/ScriptsGenerationActions';

const useStyles = makeStyles(theme => ({
	root: {},
	sectionCard: {
		marginBottom: theme.spacing(3)
	},
	sectionTitle: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
		fontWeight: 600
	},
	resultsCard: {
		marginTop: theme.spacing(2)
	},
	statusSuccess: {
		backgroundColor: '#e8f5e8',
		color: '#2e7d32'
	},
	statusFailed: {
		backgroundColor: '#ffebee',
		color: '#c62828'
	},
	tableContainer: {
		maxHeight: 400
	},
	newBadge: {
		backgroundColor: '#4caf50',
		color: 'white',
		padding: '2px 8px',
		borderRadius: '4px',
		fontSize: '0.75rem',
		fontWeight: 600,
		marginLeft: '8px'
	}
}));

const ScriptsGenerationDialogForm = (props) => {
	const { className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.scriptsGenerationReducer.formState);
	const isLoading = useSelector(state => state.scriptsGenerationReducer.isLoading);
	const isEnodebPairingLoading = useSelector(state => state.scriptsGenerationReducer.isEnodebPairingLoading);
	const processingResult = useSelector(state => state?.scriptsGenerationReducer?.processingResult);
	const enodebPairingResult = useSelector(state => state?.scriptsGenerationReducer?.enodebPairingResult);

	// Nokia Audit selectors
	const regenerationAudit = useSelector(state => state.scriptsGenerationReducer.regenerationAudit);

	// Ericsson Audit selectors
	const ericssonAudit = useSelector(state => state.scriptsGenerationReducer.ericssonAudit);

	// Alarms Diff selectors
	const alarmsDiff = useSelector(state => state.scriptsGenerationReducer.alarmsDiff);

	// Nokia Description Checks selectors
	const nokiaDescriptionChecks = useSelector(state => state.scriptsGenerationReducer.nokiaDescriptionChecks);

	// Nokia Description Diff selector
	const nokiaDescriptionDiff = useSelector(state => state.scriptsGenerationReducer.nokiaDescriptionDiff);

	const _onChangeEricssonAuditExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];

			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						ericssonAuditExcelFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({
					message: 'Please select an Excel file (.xlsx or .xls)',
					options: { variant: 'warning' }
				}));
			}
		}
	};

	const _runEricssonAudit = () => {
		if (!formState.values.ericssonAuditExcelFile) {
			dispatch(enqueueSnackbar({
				message: 'Please select an Excel file for the Ericsson Audit',
				options: { variant: 'warning' }
			}));
			return;
		}

		if (formState.values.ericssonAuditExcelFile.size > 50000000) {
			dispatch(enqueueSnackbar({
				message: 'File size cannot be larger than 50MB',
				options: { variant: 'warning' }
			}));
			return;
		}

		dispatch(RunEricssonAudit(formState.values.ericssonAuditExcelFile));
	};

	const _handleDateChange = (date) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				deploymentDate: date
			},
			touched: {
				...formState.touched,
				deploymentDate: true
			}
		});
	};

	const _onChangeExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						excelInputFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({ 
					message: 'Please select an Excel file (.xlsx or .xls)', 
					options: { variant: 'warning' } 
				}));
			}
		}
	};

	// EnodebPairing Excel file handler
	const _onChangeEnodebPairingExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						enodebPairingExcelFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({ 
					message: 'Please select an Excel file (.xlsx or .xls)', 
					options: { variant: 'warning' } 
				}));
			}
		}
	};

	// Audit Excel file handler
	const _onChangeAuditExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						auditExcelFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({ 
					message: 'Please select an Excel file (.xlsx or .xls)', 
					options: { variant: 'warning' } 
				}));
			}
		}
	};

	// Nokia Description Diff Excel file handler
	const _onChangeDescriptionDiffExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];
 
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
 
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						nokiaDescriptionDiffExcelFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({
					message: 'Please select an Excel file (.xlsx or .xls)',
					options: { variant: 'warning' }
				}));
			}
		}
	};

	// Generate Nokia Audit
	const _generateRegenerationAudit = () => {
		// Validate required fields
		if (!formState.values.deploymentDate) {
			dispatch(enqueueSnackbar({ 
				message: 'Deployment date is required', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (!formState.values.auditExcelFile) {
			dispatch(enqueueSnackbar({ 
				message: 'Please select an Excel file for audit', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (formState.values.auditExcelFile.size > 50000000) { // 50MB limit
			dispatch(enqueueSnackbar({ 
				message: 'File size cannot be larger than 50MB', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		const deploymentDate = moment(formState.values.deploymentDate).format('YYYY-MM-DDTHH:mm:ss');
		dispatch(GenerateRegenerationAudit(formState.values.auditExcelFile, deploymentDate));
	};

	const _processMocnWork = (event) => {
		// Validate required fields
		if (!formState.values.deploymentDate) {
			dispatch(enqueueSnackbar({ 
				message: 'Deployment date is required', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (!formState.values.excelInputFile) {
			dispatch(enqueueSnackbar({ 
				message: 'Please select an Excel file', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (formState.values.excelInputFile.size > 50000000) { // 50MB limit for Excel
			dispatch(enqueueSnackbar({ 
				message: 'File size cannot be larger than 50MB', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		// Create the request object with defaults
		const request = {
			deploymentDate: moment(formState.values.deploymentDate).format('YYYY-MM-DDTHH:mm:ss'),
			useDatabase: true, // Default to database mode
			scfFolderPath: "C:\\temp\\" // Default SCF folder path
		};

		dispatch(ProcessMocnWork(request, formState.values.excelInputFile));
	};

	// EnodebPairing processing function
	const _processEnodebPairingWork = (event) => {
		// Validate required fields
		if (!formState.values.deploymentDate) {
			dispatch(enqueueSnackbar({ 
				message: 'Deployment date is required', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (!formState.values.enodebPairingExcelFile) {
			dispatch(enqueueSnackbar({ 
				message: 'Please select an Excel file with SiteId and S3EnodebIdPairing columns', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (formState.values.enodebPairingExcelFile.size > 50000000) { // 50MB limit
			dispatch(enqueueSnackbar({ 
				message: 'File size cannot be larger than 50MB', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		// Create the request object with defaults
		const request = {
			deploymentDate: moment(formState.values.deploymentDate).format('YYYY-MM-DDTHH:mm:ss'),
			useDatabase: true, // Default to database mode
			scfFolderPath: "C:\\temp\\Scripts\\Output" // Default SCF folder path
		};

		dispatch(ProcessEnodebPairingWork(request, formState.values.enodebPairingExcelFile));
	};

	// Alarms Diff ZIP file handler
	const _onChangeAlarmsDiffZipFile = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];

			if (!file.name.endsWith('.zip')) {
				dispatch(enqueueSnackbar({
					message: 'Please select a ZIP file containing the pre and post alarm CSV files',
					options: { variant: 'warning' }
				}));
				return;
			}

			setFormState({
				...formState,
				values: { ...formState.values, alarmsDiffZipFile: file }
			});
		}
	};

	// Process Alarms Diff
	const _processAlarmsDiff = () => {
		if (!formState.values.alarmsDiffZipFile) {
			dispatch(enqueueSnackbar({
				message: 'Please select a ZIP file containing the pre and post alarm CSV files',
				options: { variant: 'warning' }
			}));
			return;
		}

		if (formState.values.alarmsDiffZipFile.size > 100000000) { // 100MB limit
			dispatch(enqueueSnackbar({
				message: 'File size cannot be larger than 100MB',
				options: { variant: 'warning' }
			}));
			return;
		}

		dispatch(ProcessAlarmsDiff(formState.values.alarmsDiffZipFile));
	};

	// Run Nokia Description Checks
	const _runNokiaDescriptionChecks = () => {
		dispatch(RunNokiaDescriptionChecks());
	};

	// Nokia Description Diff handler
	const _runNokiaDescriptionDiff = () => {
		if (!formState.values.nokiaDescriptionDiffExcelFile) {
			dispatch(enqueueSnackbar({
				message: 'Please select an Excel file for the Description Diff',
				options: { variant: 'warning' }
			}));
			return;
		}
 
		if (formState.values.nokiaDescriptionDiffExcelFile.size > 50000000) { // 50MB limit
			dispatch(enqueueSnackbar({
				message: 'File size cannot be larger than 50MB',
				options: { variant: 'warning' }
			}));
			return;
		}
 
		dispatch(RunNokiaDescriptionDiff(formState.values.nokiaDescriptionDiffExcelFile));
	};

	// Initialize default values if not set
	useEffect(() => {
		if (!formState.values.deploymentDate) {
			setFormState({
				...formState,
				values: {
					...formState.values,
					deploymentDate: new Date()
				}
			});
		}
	}, []);

	// Helper function to render status chip
	const renderStatusChip = (success) => (
		<Chip 
			label={success ? 'Success' : 'Failed'} 
			color={success ? 'success' : 'error'}
			size="small"
			style={{ borderRadius: '4px' }}
		/>
	);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			{/* Use Grid2 container with proper direction and spacing */}
			<Grid container direction="column" spacing={3}>
				{/* MOCN Scripts Processing Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px' }}>
						<CardHeader title="NOKIA MOCN Scripts" />
						<CardContent>
							<Grid container spacing={2}>
								{/* Deployment Date */}
								<Grid size={{ xs: 12, md: 6 }}>
									<DatePicker
										label="Deployment Date"
										name="deploymentDate"
										value={formState.values.deploymentDate || new Date()}
										onChange={_handleDateChange}
										format="YYYY/MM/DD"
										disabled={isLoading}
									/>
								</Grid>

								{/* Excel Input File */}
								<Grid size={{ xs: 12, md: 6 }}>
									<FilePicker
										placeholder="Select Excel file with site information"
										accept=".xlsx,.xls"
										onChange={_onChangeExcelFile}
										value={formState.values.excelInputFile?.name || ''}
										disabled={isLoading}
									/>
								</Grid>

								{/* Process Button */}
								<Grid size={12}>
									<Button
										onClick={_processMocnWork}
										color="primary"
										size="large"
										variant="contained"
										disabled={
											isLoading || 
											!formState.values.deploymentDate || 
											!formState.values.excelInputFile
										}
										fullWidth
									>
										{isLoading ? 'Processing...' : 'Process MOCN Work'}
									</Button>
								</Grid>
							</Grid>

							{/* MOCN Results Display */}
							{processingResult && !processingResult.auditSummary && (
								<>
									<Card variant="outlined" className={classes.resultsCard}>
										<CardContent>
											<Typography variant="h6" gutterBottom>
												MOCN Processing Results
											</Typography>
											<Grid container spacing={2}>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Total Sites
													</Typography>
													<Typography variant="h6">
														{processingResult.totalSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Processed Files
													</Typography>
													<Typography variant="h6" style={{ color: '#2e7d32' }}>
														{processingResult.processedSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Failed Files
													</Typography>
													<Typography variant="h6" style={{ color: '#c62828' }}>
														{processingResult.failedSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Status
													</Typography>
													<Typography variant="h6">
														{renderStatusChip(processingResult.success)}
													</Typography>
												</Grid>
											</Grid>

											{processingResult.error && (
												<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
													<strong>Error:</strong> {processingResult.error}
												</Typography>
											)}
										</CardContent>
									</Card>

									{/* Detailed Site Results (Collapsible) */}
									{processingResult.siteResults && processingResult.siteResults.length > 0 && (
										<Card variant="outlined" className={classes.resultsCard} style={{ marginTop: 16 }}>
											<CardContent>
												<Typography variant="h6" gutterBottom>
													Detailed Site Results
												</Typography>
												<div style={{ maxHeight: 400, overflowY: 'auto' }}>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell><strong>Site ID</strong></TableCell>
																<TableCell><strong>Status</strong></TableCell>
																<TableCell><strong>Templates</strong></TableCell>
																<TableCell><strong>Files/Error</strong></TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{processingResult.siteResults.map((site, index) => (
																<TableRow key={index}>
																	<TableCell>{site.siteId}</TableCell>
																	<TableCell>
																		{site.success ? (
																			<Chip label="✓ OK" size="small" style={{ backgroundColor: '#4caf50', color: 'white' }} />
																		) : (
																			<Chip label="✗ FAIL" size="small" style={{ backgroundColor: '#f44336', color: 'white' }} />
																		)}
																	</TableCell>
																	<TableCell>
																		{site.processedTemplates && site.processedTemplates.length > 0
																			? site.processedTemplates.join(', ')
																			: '-'}
																	</TableCell>
																	<TableCell>
																		{site.success ? (
																			<Typography variant="body2" color="textSecondary">
																				{site.generatedFiles?.length || 0} file(s)
																			</Typography>
																		) : (
																			<Typography variant="body2" color="error" style={{ fontSize: '0.75rem' }}>
																				{site.errorMessage}
																			</Typography>
																		)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</div>
											</CardContent>
										</Card>
									)}
								</>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Nokia Audit Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px', border: '2px solid #4caf50' }}>
						<CardHeader 
							title={
								<Box display="flex" alignItems="center">
									<span className={classes.newBadge}>NOKIA AUDIT</span>
									<span>
										<Typography variant="body2" style={{ marginLeft: 8 }}>
											<strong>Diff & Expected Scripts</strong>
										</Typography>
									</span>
								</Box>
							}
						/>
						<CardContent>
							<Alert severity="info" style={{ marginBottom: 16 }}>
								<Typography variant="body2">
									<strong>Process:</strong> Compares current Nokia dump with freshly regenerated scripts using the latest baseline.
								</Typography>
							</Alert>

							<Grid container spacing={2}>
								{/* Audit Excel File */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select Excel file for audit (same format as MOCN)"
										accept=".xlsx,.xls"
										onChange={_onChangeAuditExcelFile}
										value={formState.values.auditExcelFile?.name || ''}
										disabled={regenerationAudit.isLoading}
										helperText="Excel file should contain: SiteId, SelectedTemplates columns"
									/>
								</Grid>

								{/* Generate Audit Button */}
								<Grid size={12}>
									<Button
										onClick={_generateRegenerationAudit}
										color="secondary"
										size="large"
										variant="contained"
										disabled={
											regenerationAudit.isLoading || 
											!formState.values.deploymentDate || 
											!formState.values.auditExcelFile
										}
										fullWidth
									>
										{regenerationAudit.isLoading ? 'Generating Audit...' : 'Generate Audit'}
									</Button>
								</Grid>
							</Grid>

							{/* Regeneration Audit Results Display */}
							{regenerationAudit.result && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Regeneration Audit Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Processed Sites
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{regenerationAudit.result.summary?.processedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Number of Changes
												</Typography>
												<Typography variant="h6" style={{ color: '#ff9800' }}>
													{regenerationAudit.result.summary?.changesDetected || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Status
												</Typography>
												<Typography variant="h6">
													{renderStatusChip(regenerationAudit.result.success)}
												</Typography>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							)}

							{regenerationAudit.error && (
								<Alert severity="error" style={{ marginTop: 16 }}>
									<Typography variant="body2">
										<strong>Error:</strong> {regenerationAudit.error}
									</Typography>
								</Alert>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* EnodebPairing Processing Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px' }}>
						<CardHeader title="NOKIA EnodebPairing" />
						<CardContent>
							<Grid container spacing={2}>
								{/* EnodebPairing Excel File */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select Excel file with SiteId and S3EnodebIdPairing columns"
										accept=".xlsx,.xls"
										onChange={_onChangeEnodebPairingExcelFile}
										value={formState.values.enodebPairingExcelFile?.name || ''}
										disabled={isLoading}
										helperText="Excel file should contain: SiteId, S3EnodebIdPairing columns"
									/>
								</Grid>

								{/* EnodebPairing Process Button */}
								<Grid size={12}>
									<Button
										onClick={_processEnodebPairingWork}
										color="primary"
										size="large"
										variant="contained"
										disabled={
											isEnodebPairingLoading || 
											!formState.values.deploymentDate || 
											!formState.values.enodebPairingExcelFile
										}
										fullWidth
									>
										{isEnodebPairingLoading ? 'Processing...' : 'Process EnodebPairing Work'}
									</Button>
								</Grid>
							</Grid>

							{/* EnodebPairing Results Display */}
							{enodebPairingResult && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											EnodebPairing Processing Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Total Sites
												</Typography>
												<Typography variant="h6">
													{enodebPairingResult.totalSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Processed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{enodebPairingResult.processedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Failed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#c62828' }}>
													{enodebPairingResult.failedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Status
												</Typography>
												<Typography variant="h6">
													{renderStatusChip(enodebPairingResult.success)}
												</Typography>
											</Grid>
										</Grid>

										{enodebPairingResult.errorMessage && (
											<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
												<strong>Error:</strong> {enodebPairingResult.errorMessage}
											</Typography>
										)}
									</CardContent>
								</Card>
							)}
						</CardContent>
					</Card>
				</Grid>
				{/* Alarms Diff Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px', border: '2px solid #d5810d' }}>
						<CardHeader
							title={
								<Box display="flex" alignItems="center">
									<span className={classes.newBadge} style={{ backgroundColor: '#d5810d' }}>NOKIA ALARMS</span>
									<span>
										<Typography variant="body2" style={{ marginLeft: 8 }}>
											<strong>Pre/Post Diff</strong>
										</Typography>
									</span>
								</Box>
							}
						/>
						<CardContent>
							<Alert severity="info" style={{ marginBottom: 16 }}>
								<Typography variant="body2">
									<strong>Process:</strong> Upload a ZIP containing <em>filename_pre.csv</em> and <em>filename_post.csv</em>.
									The result is an Excel with 3 sheets: Pre, Post, and New alarms.
								</Typography>
							</Alert>

							<Grid container spacing={2}>
								{/* ZIP File Picker */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select ZIP file with pre and post alarm CSV files"
										accept=".zip"
										onChange={_onChangeAlarmsDiffZipFile}
										value={formState.values.alarmsDiffZipFile?.name || ''}
										disabled={alarmsDiff.isLoading}
										helperText="ZIP must contain: filename_pre.csv and filename_post.csv"
									/>
								</Grid>

								{/* Process Button */}
								<Grid size={12}>
									<Button
										onClick={_processAlarmsDiff}
										color="primary"
										size="large"
										variant="contained"
										disabled={alarmsDiff.isLoading || !formState.values.alarmsDiffZipFile}
										fullWidth
									>
										{alarmsDiff.isLoading ? 'Processing...' : 'Process Alarms Diff'}
									</Button>
								</Grid>
							</Grid>

							{/* Results */}
							{alarmsDiff.result && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Alarms Diff Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Pre Alarms</Typography>
												<Typography variant="h6">
													{alarmsDiff.result.preAlarmCount}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Post Alarms</Typography>
												<Typography variant="h6">
													{alarmsDiff.result.postAlarmCount}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">New Alarms</Typography>
												<Typography variant="h6" style={{ color: alarmsDiff.result.newAlarmCount > 0 ? '#c62828' : '#2e7d32' }}>
													{alarmsDiff.result.newAlarmCount}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Status</Typography>
												<Typography variant="h6">
													{renderStatusChip(alarmsDiff.result.success)}
												</Typography>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							)}

							{alarmsDiff.error && (
								<Alert severity="error" style={{ marginTop: 16 }}>
									<Typography variant="body2">
										<strong>Error:</strong> {alarmsDiff.error}
									</Typography>
								</Alert>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Nokia Description Checks Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px', border: '2px solid #d5810d' }}>
						<CardHeader
							title={
								<Box display="flex" alignItems="center">
									<span style={{
										backgroundColor: '#d5810d',
										color: 'white',
										padding: '2px 8px',
										borderRadius: '4px',
										fontSize: '0.75rem',
										fontWeight: 600,
										marginRight: '8px'
									}}>
										NOKIA DESCRIPTION CHECKS
									</span>
									<Typography variant="body2">
										<strong>MRBTSDESC VF3REQ</strong>
									</Typography>
								</Box>
							}
						/>
						<CardContent>
							<Alert severity="info" style={{ marginBottom: 16 }}>
								<Typography variant="body2">
									<strong>Process:</strong> Checks all VF3REQ sites in the dump for missing configuration (LNHOIF, IRFIM, PMQAP, MOPR/MOIMP/MORED, CAIMLBP, MME IP).
								</Typography>
							</Alert>

							<Button
								onClick={_runNokiaDescriptionChecks}
								size="large"
								variant="contained"
								disabled={nokiaDescriptionChecks.isLoading}
								fullWidth
								style={{ backgroundColor: '#d5810d', color: 'white' }}
							>
								{nokiaDescriptionChecks.isLoading ? 'Running Checks...' : 'Run Description Checks'}
							</Button>

							{/* Results */}
							{nokiaDescriptionChecks.result && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Description Checks Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Sites Checked
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{nokiaDescriptionChecks.result.summary?.sitesChecked || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Findings
												</Typography>
												<Typography variant="h6" style={{ color: '#ff9800' }}>
													{nokiaDescriptionChecks.result.summary?.findingsCount || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Status
												</Typography>
												<Typography variant="h6">
													{renderStatusChip(nokiaDescriptionChecks.result.success)}
												</Typography>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							)}

							{nokiaDescriptionChecks.error && (
								<Alert severity="error" style={{ marginTop: 16 }}>
									<Typography variant="body2">
										<strong>Error:</strong> {nokiaDescriptionChecks.error}
									</Typography>
								</Alert>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Nokia Description Diff Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px', border: '2px solid #d5810d' }}>
						<CardHeader
							title={
								<Box display="flex" alignItems="center">
									<span style={{
										backgroundColor: '#d5810d',
										color: 'white',
										padding: '2px 8px',
										borderRadius: '4px',
										fontSize: '0.75rem',
										fontWeight: 600,
										marginRight: '8px'
									}}>
										NOKIA DESCRIPTION DIFF
									</span>
									<Typography variant="body2">
										<strong>MRBTSDESC VF3REQ Script Diff</strong>
									</Typography>
								</Box>
							}
						/>
						<CardContent>
							<Alert severity="info" style={{ marginBottom: 16 }}>
								<Typography variant="body2">
									<strong>Process:</strong> Compares the Script column in the Excel against
									what is currently set in <code>MRBTSDESC.groupName</code> in the Nokia dump,
									and reports matches, mismatches, and sites not found.
								</Typography>
							</Alert>
 
							<Grid container spacing={2}>
								{/* Excel file picker */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select Excel file (SiteId + Script columns)"
										accept=".xlsx,.xls"
										onChange={_onChangeDescriptionDiffExcelFile}
										value={formState.values.nokiaDescriptionDiffExcelFile?.name || ''}
										disabled={nokiaDescriptionDiff.isLoading}
									/>
								</Grid>
 
								{/* Run button */}
								<Grid size={12}>
									<Button
										onClick={_runNokiaDescriptionDiff}
										size="large"
										variant="contained"
										disabled={
											nokiaDescriptionDiff.isLoading ||
											!formState.values.nokiaDescriptionDiffExcelFile
										}
										fullWidth
										style={{ backgroundColor: '#d5810d', color: 'white' }}
									>
										{nokiaDescriptionDiff.isLoading ? 'Running Diff...' : 'Run Description Diff'}
									</Button>
								</Grid>
							</Grid>
 
							{/* Results display */}
							{nokiaDescriptionDiff.result && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Description Diff Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Total Rows</Typography>
												<Typography variant="h6">
													{nokiaDescriptionDiff.result.totalRows || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Match</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{nokiaDescriptionDiff.result.matchCount || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Mismatch</Typography>
												<Typography variant="h6" style={{ color: '#c62828' }}>
													{nokiaDescriptionDiff.result.mismatchCount || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Not Found</Typography>
												<Typography variant="h6" style={{ color: '#e65100' }}>
													{nokiaDescriptionDiff.result.notFoundCount || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">Unexpected VF3REQ</Typography>
												<Typography variant="h6" style={{ color: '#e65100' }}>
													{nokiaDescriptionDiff.result.unexpectedVf3ReqCount || 0}
												</Typography>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							)}
 
							{nokiaDescriptionDiff.error && (
								<Alert severity="error" style={{ marginTop: 16 }}>
									<Typography variant="body2">
										<strong>Error:</strong> {nokiaDescriptionDiff.error}
									</Typography>
								</Alert>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Ericsson Audit Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px', border: '2px solid #1565c0' }}>
						<CardHeader
							title={
								<Box display="flex" alignItems="center">
									<span style={{
										background: '#1565c0',
										color: '#fff',
										borderRadius: 4,
										padding: '2px 8px',
										fontSize: 11,
										fontWeight: 700,
										marginRight: 8
									}}>
										ERICSSON AUDIT
									</span>
									<Typography variant="body2">
										<strong>Dump-BaselineTemplates Diff</strong>
									</Typography>
								</Box>
							}
						/>
						<CardContent>
							<Alert severity="info" style={{ marginBottom: 16 }}>
								<Typography variant="body2">
									<strong>Process:</strong> Compares current Ericsson dump against the baseline templates and generates an Excel report with all differences found.
								</Typography>
							</Alert>

							<Grid container spacing={2}>
								{/* Ericsson Audit Excel File */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select Excel file for Ericsson Audit (Site ID + Script columns)"
										accept=".xlsx,.xls"
										onChange={_onChangeEricssonAuditExcelFile}
										value={formState.values.ericssonAuditExcelFile?.name || ''}
										disabled={ericssonAudit.isLoading}
										helperText="Excel file should contain: Site ID and Script (e.g. S0, S1, S1+S2) columns"
									/>
								</Grid>

								{/* Run Ericsson Audit Button */}
								<Grid size={12}>
									<Button
										onClick={_runEricssonAudit}
										color="primary"
										size="large"
										variant="contained"
										disabled={ericssonAudit.isLoading || !formState.values.ericssonAuditExcelFile}
										fullWidth
									>
										{ericssonAudit.isLoading ? 'Running Ericsson Audit...' : 'Run Ericsson Audit'}
									</Button>
								</Grid>
							</Grid>

							{/* Ericsson Audit Results */}
							{ericssonAudit.result && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Ericsson Audit Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Sites Processed
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{ericssonAudit.result.sitesProcessed || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 4 }}>
												<Typography variant="body2" color="textSecondary">
													Differences Found
												</Typography>
												<Typography variant="h6" style={{ color: ericssonAudit.result.totalDifferences > 0 ? '#c62828' : '#2e7d32' }}>
													{ericssonAudit.result.totalDifferences || 0}
												</Typography>
											</Grid>
										</Grid>
										<Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
											The Excel report has been downloaded automatically.
										</Typography>
									</CardContent>
								</Card>
							)}

							{/* Ericsson Audit Error */}
							{ericssonAudit.error && (
								<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
									<strong>Error:</strong> {ericssonAudit.error}
								</Typography>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

ScriptsGenerationDialogForm.propTypes = {
	className: PropTypes.string
};

export default ScriptsGenerationDialogForm;